import React, { useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import { FormControl } from 'react-bootstrap'
import { GlgButton, GlgCheckbox, GlgLoadingSpinner, GlgTooltip } from 'SharedComponents'
import ClearFilters from '../filters/clear_filters'
import { Col } from 'react-flexbox-grid'

const LocationFilter = ({
  value,
  onChange,
  showLocationFilter,
  currentLocation,
  metricUnits,
  disabled,
}) => {
  const [location, setLocation] = useState('')
  const [locationIsLoading, setLocationIsLoading] = useState(false)
  const [locationLoaded, setLocationLoaded] = useState(!!currentLocation || !!value.coords.city)
  const [radius, setRadius] = useState(value.radius || '200')
  const [open, setOpen] = useState(false)
  const [updated, setUpdated] = useState(false)

  // Update based on coords
  useEffect(() => {
    if (value && value.coords.lat && !updated) {
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${value.coords.lat},${value.coords.lng}&key=${process.env.GOOGLE_MAP_KEY}`
      )
        .then(response => response.json())
        .then(json => {
          if (json.status === 'OK') {
            const resultAddressComponents = json.results[0].address_components.filter(component =>
              component.types.includes('locality')
            )
            setLocation(resultAddressComponents[0].long_name)
            setUpdated(true)
            setOpen(true)
          }
        })
    }
  }, [value, updated])

  const errorLocation = () => {
    setLocationIsLoading(false)
    setLocationLoaded(true)
  }

  const getLocation = (position) => {
      let currentLoc
      if (currentLocation) {
        currentLoc = currentLocation
        setLocation(currentLoc)
      } else if (!value.coords.lat) {
        fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.coords.latitude},${position.coords.longitude}&key=${process.env.GOOGLE_MAP_KEY}`
        )
          .then(response => response.json())
          .then(json => {
            if (json.status === 'OK') {
              const resultAddressComponents = json.results[0].address_components.filter(component =>
                component.types.includes('locality')
              )
              currentLoc = resultAddressComponents[0].long_name
              setLocation(currentLoc)
              setLocationIsLoading(false)
              setLocationLoaded(true)
              onChange('currentLocation', currentLoc)
            }
          })
      }
    }

  const handleClick = () => {
    if (disabled) return 
    if (!locationLoaded) {
      setLocationIsLoading(true)
    } else {
      const loc = value.city || location || currentLocation
      setLocation(loc)
    }

    if (navigator.geolocation && !locationLoaded) {
      navigator.geolocation.getCurrentPosition(getLocation, errorLocation)
    }

    setOpen(!open)

    if (open) {
      onChange('location', { coords: { lat: '', lng: '' }, radius: '' })
    }
  }

  const handleChange = () => {
    if (location) {
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${location}&key=${process.env.GOOGLE_MAP_KEY}`
      )
        .then(response => response.json())
        .then(json => {
          if (json.status === 'OK') {
            const computedRadius = metricUnits === 'Meters'
              ? parseInt(radius / 1.609)
              : radius
            onChange('location', {
              coords: json.results[0].geometry.location,
              radius: computedRadius,
              city: location
            })
          }
        })
    } else {
      onChange('location', { coords: { lat: '', lng: '' }, radius: '' })
    }
  }

  const locationAdvancedClass = open ? 'inline-box' : 'location-advanced-hidden'
  const locationClass = open
    ? 'border-right-location add-margin-right-20-xs'
    : 'add-margin-right-20'
  const clearFiltersClass = open ? 'add-margin-top-10-sm' : ''

  return (
    <Col md={12}>
      {showLocationFilter && (
        <div className="pull-left visible-radius-xs add-margin-top-10">
          <div className="inline-box location-filter">
            <GlgTooltip
              tooltip={!window.I18n ? '' : window.I18n.t('customer_directories.filters.location_filter_tooltip')}
            >
              <div
                className={`${locationClass} add-margin-top-10 location inline-box`}
                onClick={handleClick}
              >
                {!window.I18n
                  ? ''
                  : window.I18n.t('customer_directories.filters.location_filter_title')}
                <div className="location-checkbox">
                  <GlgCheckbox
                    name="copy_options[]"
                    value="copy_player_roster"
                    checked={open}
                    onChange={handleClick}
                    className={'location-checkbox'}
                    disabled={disabled}
                  />
                </div>
              </div>
            </GlgTooltip>
          </div>
        </div>
      )}
      <div className="pull-right visible-radius-xs add-margin-top-10">
        <ClearFilters onChange={onChange} />
      </div>
      <Col md={12}>
        <div className="visible-radius-xs">
          <div className="text-center">
            <div className={locationAdvancedClass}>
              <div className="radius-color add-margin-left-5 add-margin-top-10">
                {!window.I18n
                  ? ''
                  : window.I18n.t('customer_directories.filters.location_filter.display_within')}
              </div>
            </div>
            <div className={`${locationAdvancedClass} location-container-xs`}>
              <FormControl
                type="number"
                value={radius}
                onChange={e => setRadius(e.target.value)}
                className={'radius-input location-text-input'}
              />
              <div className="add-margin-top-10 radius-second">
                {metricUnits === 'Meters'
                  ? !window.I18n
                    ? ''
                    : window.I18n.t('customer_directories.filters.location_filter.kilometers_of')
                  : !window.I18n
                    ? ''
                    : window.I18n.t('customer_directories.filters.location_filter.miles_of')}
              </div>
              <FormControl
                type="text"
                value={location}
                onChange={e => setLocation(e.target.value)}
                className={'location-input location-text-input'}
              />
              <div className="go-button-container">
                <GlgButton
                  text={
                    !window.I18n
                      ? ''
                      : window.I18n.t('customer_directories.filters.location_filter.go')
                  }
                  onClick={handleChange}
                  color="gray"
                  size="normal"
                  className={'go-button'}
                  disabled={locationIsLoading || disabled}
                />
              </div>
            </div>
          </div>
        </div>
      </Col>

      {showLocationFilter && (
        <div className="pull-left hidden-directories-xs">
          <div className="location-filter inline-box">
            <GlgTooltip
              tooltip={!window.I18n ? '' : window.I18n.t('customer_directories.filters.location_filter_tooltip')}
            >
              <div
                className={`${locationClass} add-margin-top-10 location inline-box`}
                onClick={handleClick}
                disabled={disabled}
              >
                {!window.I18n
                  ? ''
                  : window.I18n.t('customer_directories.filters.location_filter_title')}
                <div className="location-checkbox">
                  <GlgCheckbox
                    name="copy_options[]"
                    value="copy_player_roster"
                    checked={open}
                    onChange={handleClick}
                    disabled={disabled}
                  />
                </div>
              </div>
            </GlgTooltip>
            <div className={locationAdvancedClass}>
              <div className="add-margin-top-10 radius-first">
                {!window.I18n
                  ? ''
                  : window.I18n.t('customer_directories.filters.location_filter.display_within')}
              </div>
              <FormControl
                type="number"
                value={radius}
                onChange={e => setRadius(e.target.value)}
                className={'radius-input location-text-input'}
              />
              <div className="add-margin-top-10 radius-second">
                {metricUnits === 'Meters'
                  ? !window.I18n
                    ? ''
                    : window.I18n.t('customer_directories.filters.location_filter.kilometers_of')
                  : !window.I18n
                    ? ''
                    : window.I18n.t('customer_directories.filters.location_filter.miles_of')}
              </div>
              <GlgLoadingSpinner
                class="add-margin-right-5"
                show={locationIsLoading}
                width={25}
                color={'#5092fc'}
              />
              {!locationIsLoading && (
                <FormControl
                  type="text"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder=''
                  className={'location-input location-text-input'}
                />
              )}
              <div className="go-button-container">
                <GlgButton
                  text={
                    !window.I18n
                      ? ''
                      : window.I18n.t('customer_directories.filters.location_filter.go')
                  }
                  onClick={handleChange}
                  color="gray"
                  size="normal"
                  className={'go-button'}
                  disabled={locationIsLoading || disabled}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={`pull-right hidden-directories-xs ${clearFiltersClass}`}>
        <ClearFilters onChange={onChange} />
      </div>
    </Col>
  )
}

LocationFilter.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  showLocationFilter: PropTypes.bool,
  currentLocation: PropTypes.string,
  metricUnits: PropTypes.string,
  disabled: PropTypes.bool,
}

export default LocationFilter
